<template>
  <b-modal
    ref="modal"
    title="Additional Contacts"
    centered
    scrollable
    ok-title="Apply"
    cancel-variant="outline-secondary"
    @ok="apply"
  >
    <div v-if="isLoading" class="stcm-loading">
      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading account contacts...
    </div>
    <template v-else>
      <b-form-input
        v-if="rows.length > 8"
        v-model="filterText"
        size="sm"
        placeholder="filter by name or email"
        class="stcm-filter"
      />
      <div v-if="!rows.length" class="stcm-empty">No account contacts found.</div>
      <div v-else class="stcm-list">
        <div v-for="group in groupedRows" :key="group.key" class="stcm-group">
          <!-- role header doubles as a bulk toggle: check to select every
               (visible) user with that role, indeterminate when only some
               are selected -->
          <label class="stcm-group-head" :class="{ 'stcm-group-head--external': group.external }">
            <b-form-checkbox
              :checked="isGroupAllSelected(group)"
              :indeterminate="isGroupPartlySelected(group)"
              class="stcm-check"
              @change="toggleGroup(group)"
            />
            <span class="stcm-group-label">
              <i v-if="groupIcon(group)" class="fa" :class="groupIcon(group)" aria-hidden="true"></i>
              {{ groupLabel(group) }}
            </span>
            <span class="stcm-group-count">{{ groupSelectedCount(group) }}/{{ group.rows.length }}</span>
          </label>
          <label
            v-for="row in group.rows"
            :key="row.username"
            class="stcm-row"
            :class="{ 'stcm-row--selected': selected.includes(row.username) }"
          >
            <b-form-checkbox v-model="selected" :value="row.username" class="stcm-check"/>
            <span class="stcm-who">
              <span class="stcm-name">{{ rowName(row) }}</span>
              <span v-if="rowName(row) !== row.username" class="stcm-email" :title="row.username">{{ row.username }}</span>
            </span>
          </label>
        </div>
        <div v-if="filterText && !filteredRows.length" class="stcm-empty">No contacts match "{{ filterText }}".</div>
      </div>
      <!-- the legacy field accepted free-typed emails, so people outside the
           account (brokers, vendor reps) may need to be CC'd — keep that
           escape hatch here rather than silently dropping the capability. -->
      <div class="stcm-external-add">
        <b-form-input
          v-model="externalEmail"
          type="email"
          size="sm"
          placeholder="add an outside email (not an account user)"
          @keydown.enter.prevent="addExternal"
        />
        <b-button size="sm" variant="outline-secondary" :disabled="!externalEmailValid" @click="addExternal">Add</b-button>
      </div>
      <!-- a half-typed address can't be flushed on Apply the way a valid one
           can, so say so rather than dropping it without a word. -->
      <div v-if="externalEmail.trim() && !externalEmailValid" class="stcm-external-warn">
        "{{ externalEmail.trim() }}" isn't a valid email — it won't be added.
      </div>
      <div class="stcm-count">{{ selected.length }} selected</div>
    </template>
  </b-modal>
</template>

<script>
import { get_contact_users } from '../networking/apis'
import { roleLabelForCustomer, roleIconForCustomer } from '@/helpers/role_display'

// display order for the role groups — tenant-side admins first, then the
// people-managing / operational roles, then everything else alphabetically
// (unknown roles land after these), with External always last.
const ROLE_GROUP_ORDER = [
  'customer-admin', 'tbn-admin', 'manager', 'dispatch', 'customer',
  'booking-customer', 'customer-care', 'admin',
]

// contact picker for the support ticket Additional Contacts field, shared by
// the create form and the details edit view. parent owns the legacy
// comma-separated string; this modal parses it on open() and emits the
// re-joined string on Apply, so storage format and save paths are unchanged.
export default {
  name: 'SupportTicketContactsModal',
  props: {
    value: { type: String, default: '' },
    tenantId: { type: [String, Number], default: null },
  },
  data () {
    return {
      contacts: [],
      loadedTenantId: null,
      isLoading: false,
      selected: [],
      // emails present in the incoming value (or typed below) that aren't
      // account users — rendered as extra selectable rows tagged External
      externalRows: [],
      externalEmail: '',
      filterText: '',
    }
  },
  computed: {
    rows() {
      return [...this.contacts, ...this.externalRows]
    },
    filteredRows() {
      const q = this.filterText.trim().toLowerCase()
      if (!q) return this.rows
      return this.rows.filter(row =>
        (this.rowName(row) || '').toLowerCase().includes(q) ||
        (row.username || '').toLowerCase().includes(q))
    },
    externalEmailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.externalEmail.trim())
    },
    // account contacts bucketed by role (externals in their own trailing
    // group), built from filteredRows so the filter box narrows groups too
    // and empty groups drop out entirely
    groupedRows() {
      const byKey = {}
      const groups = []
      for (const row of this.filteredRows) {
        const key = row.external ? '__external__' : (row.role || '__none__')
        if (!byKey[key]) {
          byKey[key] = { key, external: !!row.external, role: row.external ? null : row.role, rows: [] }
          groups.push(byKey[key])
        }
        byKey[key].rows.push(row)
      }
      return groups.sort((a, b) => this.groupSortKey(a) - this.groupSortKey(b))
    },
  },
  methods: {
    async open() {
      this.filterText = ''
      this.externalEmail = ''
      this.selected = [...new Set(
        String(this.value || '').split(',').map(s => s.trim()).filter(Boolean)
      )]
      this.$refs.modal.show()
      await this.loadContacts()
      const known = new Set(this.contacts.map(c => c.username))
      this.externalRows = this.selected
        .filter(email => !known.has(email))
        .map(email => ({ username: email, external: true }))
    },
    async loadContacts() {
      // cache per tenant so re-opening the modal doesn't refetch, but an
      // admin switching accounts on the create form gets a fresh list
      if (this.loadedTenantId === this.tenantId && this.contacts.length) return
      this.isLoading = true
      try {
        const {value: {data = {}}} = await get_contact_users(this.tenantId)
        this.contacts = data.results || []
        this.loadedTenantId = this.tenantId
      } catch (e) {
        this.contacts = []
      } finally {
        this.isLoading = false
      }
    },
    rowName(row) {
      if (row.external || !row.first_name) return row.username
      return `${row.first_name} ${row.last_name || ''}`.trim()
    },
    groupSortKey(group) {
      if (group.external) return ROLE_GROUP_ORDER.length + 1
      const i = ROLE_GROUP_ORDER.indexOf(group.role)
      return i === -1 ? ROLE_GROUP_ORDER.length : i
    },
    groupLabel(group) {
      if (group.external) return 'External'
      return roleLabelForCustomer(group.role) || 'Other'
    },
    groupIcon(group) {
      if (group.external) return 'fa-user-o'
      return roleIconForCustomer(group.role)
    },
    groupSelectedCount(group) {
      return group.rows.filter(row => this.selected.includes(row.username)).length
    },
    isGroupAllSelected(group) {
      return group.rows.length > 0 && this.groupSelectedCount(group) === group.rows.length
    },
    isGroupPartlySelected(group) {
      const count = this.groupSelectedCount(group)
      return count > 0 && count < group.rows.length
    },
    toggleGroup(group) {
      // operates on the group's *visible* rows, so with a filter active the
      // bulk toggle only touches what the user can see
      const usernames = group.rows.map(row => row.username)
      if (this.isGroupAllSelected(group)) {
        this.selected = this.selected.filter(u => !usernames.includes(u))
      } else {
        const missing = usernames.filter(u => !this.selected.includes(u))
        this.selected = [...this.selected, ...missing]
      }
    },
    addExternal() {
      if (!this.externalEmailValid) return
      const email = this.externalEmail.trim()
      if (!this.rows.some(row => row.username === email)) {
        this.externalRows = [...this.externalRows, { username: email, external: true }]
      }
      if (!this.selected.includes(email)) {
        this.selected = [...this.selected, email]
      }
      this.externalEmail = ''
    },
    apply() {
      // flush a typed-but-not-Added email. people type the address and go
      // straight for Apply without noticing the Add button, and silently
      // dropping it reads as "the contact didn't save".
      if (this.externalEmailValid) this.addExternal()
      // emit in row order (account contacts first, externals after) so the
      // stored string is stable regardless of click order
      const ordered = this.rows
        .map(row => row.username)
        .filter(username => this.selected.includes(username))
      this.$emit('input', ordered.join(','))
    },
  },
}
</script>

<style scoped>
.stcm-loading,
.stcm-empty {
  color: #888;
  font-size: 13px;
  padding: 8px 2px;
  text-align: left;
}
.stcm-filter {
  margin-bottom: 8px;
}
.stcm-list {
  display: flex;
  flex-direction: column;
  max-height: 45vh;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.stcm-group:last-child .stcm-row:last-child {
  border-bottom: none;
}
/* role header row — sticky within the scrolling list so the current
   group's bulk toggle stays reachable while scrolling its members */
.stcm-group-head {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 6px 10px;
  background: #f0f4f8;
  border-bottom: 1px solid #dfe6ec;
  cursor: pointer;
  text-align: left;
}
.stcm-group-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4a6076;
}
.stcm-group-count {
  flex-shrink: 0;
  font-size: 11px;
  color: #8a9aa9;
}
.stcm-group-head--external {
  background: #fcf8ef;
  border-bottom-color: #ecdfc3;
}
.stcm-group-head--external .stcm-group-label {
  color: #8a6d3b;
}
.stcm-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 7px 10px 7px 22px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  font-weight: normal;
  text-align: left;
}
.stcm-row:hover {
  background: #f5f8fb;
}
.stcm-row--selected {
  background: #eef3f8;
}
.stcm-check {
  flex-shrink: 0;
  /* bootstrap-vue checkbox reserves label space we don't use */
  margin-right: -8px;
}
.stcm-who {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.stcm-name {
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stcm-email {
  font-size: 11px;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stcm-external-add {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}
.stcm-external-warn {
  margin-top: 4px;
  font-size: 11px;
  color: #a94442;
  text-align: left;
}
.stcm-count {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
  text-align: left;
}
</style>
